import { createContext, useContext, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type DashboardNavigationContextType = {
    canGoBack: boolean;
    goBack: () => void;
    getCurrentTitle: () => string;
    isSubPage: boolean;
};

const DashboardNavigationContext = createContext<DashboardNavigationContextType | undefined>(undefined);

export const useDashboardNavigation = () => {
    const context = useContext(DashboardNavigationContext);
    if (!context) {
        throw new Error('useDashboardNavigation must be used within DashboardNavigationProvider');
    }
    return context;
};

type DashboardNavigationProviderProps = {
    children: ReactNode;
    getTitleForPath: (pathname: string) => string;
};

export const DashboardNavigationProvider = ({ children, getTitleForPath }: DashboardNavigationProviderProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Check if we're on a sub-page (not the base dashboard routes)
    // Sub-pages have more than 2 path segments after /freelancer-dashboard
    const pathParts = location.pathname.split('/').filter(Boolean);
    const isSubPage = pathParts.length > 2 && pathParts[0] === 'freelancer-dashboard';

    const canGoBack = isSubPage;

    const goBack = () => {
        navigate(-1);
    };

    const getCurrentTitle = () => {
        return getTitleForPath(location.pathname);
    };

    return (
        <DashboardNavigationContext.Provider value={{ canGoBack, goBack, getCurrentTitle, isSubPage }}>
            {children}
        </DashboardNavigationContext.Provider>
    );
};

